import React, {
    useRef,
    useEffect,
    useCallback,
    useState,
    useContext,
} from 'react'
import { observer } from 'mobx-react'
import useMouse from '@react-hook/mouse-position'
import {
    format,
    startOfWeek,
    endOfWeek,
    addDays,
    addWeeks,
    subWeeks,
    differenceInDays,
    isSameMonth,
    getWeek,
    isSameWeek,
    isSameDay,
} from 'date-fns'
import calendarState from './CalendarState'
import 'react-calendar/dist/Calendar.css'
import { useGesture } from 'react-use-gesture'
import _ from 'underscore'
import { Button } from 'antd'
import SessionStore from '../../State/SessionStore'
import StaffCollection from '../../State/Collections/StaffCollection'
import tuple from 'immutable-tuple'
import TimeEntryCollection from '../../State/Collections/TimeEntryCollection'
import DailyAllocationCollection from '../../State/Collections/DailyAllocationCollection'
import DataStore from '../../State/DataStore'
import LayoutStore from '../../State/LayoutStore'
import { toJS } from 'mobx'
import {
    canEditStaffTime,
    canEditStaffAllocations,
} from '../../State/Permissions/HasPermissions'
import { StaffSelector } from '../Selector'
import DateValue from '../Widgets/DateValue'
import classNames from 'classnames'
import { useNavigate } from '@tanstack/react-router'

export const Calendar = observer(({ mode = 'time', date, staffId }) => {
    const navigate = useNavigate()
    useEffect(() => {
        LayoutStore.toggleSidebar(true)
    }, [])
    const settings = SessionStore.organisation.settings
    const [showCal, setShowCal] = useState(false)
    const ref = React.useRef()
    const mouse = useMouse(ref, {
        enterDelay: 100,
        leaveDelay: 100,
    })
    const calDrag = useGesture({
        onDragStart: (state) => {
            calendarState.deselectItem()
            calendarState.setCalendarMouseData(
                mouse.x,
                mouse.y,
                mouse.elementWidth,
                mouse.elementHeight
            )
        },
        onDrag: (state) => {
            if (!calendarState.newItem) {
                if (
                    state.movement[1] / (calendarState.calendarMinuteEms * 10) >
                    7.5
                ) {
                    calendarState.createNewItem(true)
                }
            } else {
                const numMinutes =
                    calendarState.calendarMouseFloatMinutes -
                    calendarState.newItem.startMinutes
                calendarState.updateNewItem({
                    numMinutes,
                })
            }
        },
        onDragEnd: (state) => {
            if (calendarState.newItem) {
                let numMinutes =
                    calendarState.calendarMouseFloatMinutes -
                    calendarState.newItem.startMinutes
                const newItem = calendarState.commitNewItem(
                    {
                        numMinutes,
                    },
                    true
                )
                setTimeout(() => {
                    calendarState.selectItem(newItem)
                }, 100)
            }
        },
    })
    const [calendarRef, setCalendarRef] = useState(null)
    const [calendarBounds, setCalendarBounds] = useState({})
    const [calendarMouseDown, setCalendarMouseDown] = useState(false)
    const [calendarMouseDownPos, setCalendarMouseDownPos] = useState({
        x: 0,
        y: 0,
    })

    useEffect(() => {
        // scroll calendar to 7:30am
        calendarState.init(mode, { date, staffId })
        ref.current.parentNode.scrollTop =
            calendarState.calendarMinuteEms * 7.75 * 60 * 16
        DataStore.setAutosave(true)
        return () => DataStore.setAutosave(false)
    }, [])
    const weekEndSameMonth = isSameMonth(
        calendarState.weekStart,
        calendarState.weekEnd
    )
    return (
        <div className="calendar" style={{ fontSize: '1rem' }}>
            <div className="not-calendar" style={{ textAlign: 'center' }}>
                <h3>{`${
                    mode === 'time' ? 'Time Entry Calendar' : 'Weekly Planning'
                }`}</h3>
                <h1>
                    {`${format(
                        calendarState.weekStart,
                        weekEndSameMonth ? 'dd' : 'dd MMM'
                    )}${weekEndSameMonth ? '-' : ' - '}${format(
                        calendarState.weekEnd,
                        weekEndSameMonth ? 'dd MMMM yyyy' : 'dd MMM yyyy'
                    )}`}
                </h1>
                <div className="flex items-center justify-center">
                    <button
                        className="ant-btn"
                        onClick={(e) => {
                            calendarState.setWeekStart(
                                subWeeks(calendarState.weekStart, 1, {
                                    weekStartsOn: 1,
                                })
                            )
                        }}
                    >
                        {'<'}
                    </button>
                    <DateValue
                        value={calendarState.weekStart}
                        onChange={(date) => {
                            calendarState.setWeekStart(date)
                        }}
                        style={{ width: '10rem', display: 'inline-block' }}
                    />
                    <button
                        className="ant-btn"
                        onClick={(e) => {
                            calendarState.setWeekStart(
                                addWeeks(calendarState.weekStart, 1, {
                                    weekStartsOn: 1,
                                })
                            )
                        }}
                    >
                        {'>'}
                    </button>
                </div>
            </div>
            {(mode === 'time' && canEditStaffTime(SessionStore.user)) ||
            (mode === 'allocation' &&
                canEditStaffAllocations(SessionStore.user)) ? (
                <div className="not-calendar pl-[4rem] pb-[1rem] max-w-max">
                    <StaffSelector
                        className="timesheet__staff-selector"
                        selectedStaffMember={
                            StaffCollection.staffById[
                                calendarState.selectedValues.staffId
                            ]
                        }
                        allowNull={false}
                        onChange={(staff) => {
                            calendarState.updateSelectedStaffId(staff.id)
                            navigate({
                                search: (prev) => ({
                                    ...prev,
                                    staffId: staff.id,
                                }),
                            })
                        }}
                        variant="secondary"
                    />
                </div>
            ) : null}
            <div
                className="flex flex-col"
                style={{
                    height: `${window.innerHeight - 150}px`,
                }}
            >
                <div className="flex" style={{ flex: '0 0 auto' }}>
                    <div
                        className="w-[4rem]"
                        style={{
                            flex: '0 0 auto',
                            height: `${calendarState.calendarMinuteEms * 55}rem`,
                        }}
                    />
                    <div
                        className={classNames(
                            'flex flex-1 border border-[#ddd]',
                            mode === 'time' ? 'bg-[#f0f0f0]' : 'bg-[#666]'
                        )}
                        ref={ref}
                        style={{
                            height: `${calendarState.calendarMinuteEms * 55}rem`,
                            borderRadius: '1rem 1rem 0 0',
                        }}
                    >
                        {calendarState.weekDays.map((day, i) => {
                            return (
                                <div
                                    className="flex items-center justify-center h-full text-center"
                                    style={{
                                        width: `${100 / 7}%`,
                                        color: [5, 6].includes(i)
                                            ? mode === 'time'
                                                ? '#888'
                                                : '#ccc'
                                            : mode === 'time'
                                              ? '#444'
                                              : '#fff',
                                    }}
                                >
                                    <div>
                                        <div className="text-[1.75rem] mt-1.5 font-semibold">
                                            {`${format(day, 'dd')}`}
                                        </div>
                                        <div className="uppercase text-[0.8rem] mt-1.5 font-normal">
                                            {`${format(day, 'EEEE')}`}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
                <div className="flex" style={{ flex: '0 0 auto' }}>
                    <div
                        className="w-[4rem]"
                        style={{
                            flex: '0 0 auto',
                            height: `${calendarState.calendarMinuteEms * 20}rem`,
                        }}
                    />
                    <div
                        ref={ref}
                        className="flex flex-1 border-b border-b-[#ddd] bg-[#f0f0f0]"
                        style={{
                            height: `${calendarState.calendarMinuteEms * 20}rem`,
                        }}
                    >
                        {calendarState.weekDays.map((day, i) => {
                            return (
                                <div
                                    className="h-full text-center flex items-center justify-center"
                                    style={{
                                        width: `${100 / 7}%`,
                                        color: [5, 6].includes(i)
                                            ? '#888'
                                            : '#444',
                                    }}
                                >
                                    <div>
                                        <div className="text-sm font-extrabold">
                                            {`${
                                                calendarState.dayTotals[i].hours
                                            }:${
                                                (calendarState.dayTotals[i]
                                                    .minutes < 10
                                                    ? '0'
                                                    : '') +
                                                calendarState.dayTotals[i]
                                                    .minutes
                                            }`}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
                <div className="flex flex-1 overflow-auto">
                    <svg
                        className="w-[4em] bg-transparent"
                        style={{
                            flex: '0 0 auto',
                            height: `${
                                calendarState.calendarMinuteEms * 60 * 24
                            }rem`,
                        }}
                    >
                        {[...Array(24 - 1).keys()].map((d) => {
                            const h24 = d + 1
                            const h12 = h24 > 12 ? h24 - 12 : h24
                            const ampm = h24 >= 12 ? 'pm' : 'am'
                            return (
                                <text
                                    style={{
                                        fontSize: '1rem',
                                        fill: '#888',
                                    }}
                                    x={`85%`}
                                    y={`${
                                        (d + 1) *
                                            calendarState.calendarMinuteEms *
                                            60 +
                                        0.25
                                    }rem`}
                                    textAnchor={`end`}
                                >{`${h12} ${ampm}`}</text>
                            )
                        })}
                    </svg>
                    <div
                        className="flex-1"
                        style={{
                            height: `${
                                calendarState.calendarMinuteEms * 60 * 24
                            }rem`,
                        }}
                        ref={ref}
                        onPointerMove={(e) => {
                            calendarState.setCalendarMouseData(
                                mouse.x,
                                mouse.y,
                                mouse.elementWidth,
                                mouse.elementHeight
                            )
                        }}
                    >
                        {(
                            DailyAllocationCollection
                                .dailyAllocationsByStaffIdWeek[
                                tuple(
                                    calendarState.selectedValues.staffId,
                                    getWeek(calendarState.weekStart, {
                                        weekStartsOn: 1,
                                    })
                                )
                            ] || []
                        ).map((al) => {
                            return (
                                <CalendarAllocation
                                    key={al.id}
                                    allocation={al}
                                    mouse={mouse}
                                />
                            )
                        })}
                        {(
                            TimeEntryCollection.timeEntries.filter(
                                (te) =>
                                    te.numMinutes > 0 &&
                                    te.staffId ===
                                        calendarState.selectedValues.staffId &&
                                    isSameWeek(
                                        te.date,
                                        calendarState.weekStart,
                                        { weekStartsOn: 1 }
                                    ) &&
                                    !te.deletedAt
                            ) || []
                        ).map((te) => {
                            return (
                                <CalendarTimeEntry
                                    key={te.id}
                                    timeEntry={te}
                                    mouse={mouse}
                                />
                            )
                        })}
                        {calendarState.newItem &&
                            (calendarState.mode === 'allocation' ? (
                                <CalendarAllocation
                                    key={calendarState.newItem.id}
                                    allocation={calendarState.newItem}
                                    mouse={mouse}
                                />
                            ) : (
                                <CalendarTimeEntry
                                    key={calendarState.newItem.id}
                                    timeEntry={calendarState.newItem}
                                    mouse={mouse}
                                />
                            ))}
                        <svg
                            style={{
                                height: `${
                                    calendarState.calendarMinuteEms * 60 * 24
                                }rem`,
                                width: '100%',
                                background: 'white',
                                borderRadius: '0 0 1rem 1rem',
                                zIndex: 1,
                                fontSize: '1rem',
                            }}
                            onClick={(e) => {
                                calendarState.deselectItem()
                            }}
                            {...calDrag()}
                        >
                            <defs>
                                <pattern
                                    id="hatchtile"
                                    x="-5"
                                    y="-5"
                                    patternUnits="userSpaceOnUse"
                                    height="5"
                                    width="5"
                                >
                                    <image
                                        x="0"
                                        y="0"
                                        height="5"
                                        width="5"
                                        xlinkHref={`${new URL(
                                            '/public/hatchtile.png',
                                            import.meta.url
                                        )}`}
                                    />
                                </pattern>
                            </defs>
                            {[...Array(7).keys()].map((d) => {
                                return calendarState.isDayIndexWeekend(d) ? (
                                    <rect
                                        width={`${100 / 7}%`}
                                        height="100%"
                                        x={`${d * (100 / 7)}%`}
                                        y="0"
                                        fill="url(#hatchtile)"
                                        opacity="1"
                                    />
                                ) : null
                            })}
                            {[...Array((24 * 60) / 15 - 1).keys()].map((d) => {
                                return (
                                    <line
                                        x1={`0%`}
                                        y1={`${
                                            (d + 1) *
                                            calendarState.calendarMinuteEms *
                                            15
                                        }rem`}
                                        x2={`100%`}
                                        y2={`${
                                            (d + 1) *
                                            calendarState.calendarMinuteEms *
                                            15
                                        }rem`}
                                        stroke={
                                            (d + 1) % 4 ? '#f0f0f0' : '#ddd'
                                        }
                                    />
                                )
                            })}
                            {[...Array(6).keys()].map((d) => {
                                return (
                                    <line
                                        x1={`${(d + 1) * (100 / 7)}%`}
                                        y1="0%"
                                        x2={`${(d + 1) * (100 / 7)}%`}
                                        y2="100%"
                                        stroke="#ddd"
                                    />
                                )
                            })}
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    )
})

const CalendarAllocation = observer(({ allocation, mouse }) => {
    return (
        <CalendarBlock
            item={allocation}
            itemType="allocation"
            zIndex={calendarState.mode === 'allocation' ? 3 : 2}
            align={'left'}
            opacity={calendarState.mode === 'allocation' ? 1 : 0.5}
            mouse={mouse}
        />
    )
})

const CalendarTimeEntry = observer(({ timeEntry, mouse }) => {
    return (
        <CalendarBlock
            key={timeEntry.id}
            item={timeEntry}
            itemType="time"
            zIndex={calendarState.mode === 'time' ? 3 : 2}
            align={'right'}
            opacity={calendarState.mode === 'time' ? 1 : 0.5}
            mouse={mouse}
        />
    )
})

const CalendarBlock = observer(
    ({ item, itemType, zIndex, align, opacity, mouse, ...props }) => {
        const disabled = itemType !== calendarState.mode
        const settings = SessionStore.organisation.settings
        const diffDays = differenceInDays(item.date, calendarState.weekStart)
        const [hovering, setHovering] = useState(false)
        const [changeY, setChangeY] = useState(0)
        const [changeLength, setChangeLength] = useState(0)
        const [clickOffset, setClickOffset] = useState(0)
        const [activeDragType, setActiveDragType] = useState(null)
        const [dragDayIndex, setDragDayIndex] = useState(-1)
        const y = changeY + item.startMinutes
        const length = changeLength + item.numMinutes
        const selected = item === calendarState.selectedItem
        const startDrag = useGesture({
            onDragStart: () => {
                if (activeDragType !== null) return
                setActiveDragType('start')
                calendarState.setDragging(true)
                calendarState.selectItem(item, itemType)
            },
            onDrag: (state) => {
                if (activeDragType !== 'start') return
                const newY = calendarState.calendarMouseFloatMinutes
                const newStartMinutes = newY
                const newLength = item.startMinutes + item.numMinutes - newY
                setChangeY(newY - item.startMinutes)
                setChangeLength(
                    Math.max(newLength - item.numMinutes, -item.numMinutes + 15)
                )
                calendarState.adjustDragMinutes(changeLength)
            },
            onDragEnd: (state) => {
                if (activeDragType !== 'start') return
                let oldStartMinutes = item.startMinutes
                let newStartMinutes = changeY + item.startMinutes
                let newNumMinutes =
                    item.numMinutes - (newStartMinutes - oldStartMinutes)
                calendarState.editSelectedItem(
                    {
                        startMinutes: newStartMinutes,
                        numMinutes: Math.max(newNumMinutes, 15),
                    },
                    true
                )
                calendarState.adjustDragMinutes(0)
                setChangeY(0)
                setChangeLength(0)
                calendarState.setDragging(false)
                setActiveDragType(null)
            },
        })
        const endDrag = useGesture({
            onDragStart: () => {
                if (activeDragType !== null) return
                setActiveDragType('end')
                calendarState.setDragging(true)
                calendarState.selectItem(item, itemType)
            },
            onDrag: (state) => {
                if (activeDragType !== 'end') return
                const newLength = calendarState.calendarMouseFloatMinutes - y
                calendarState.adjustDragMinutes(newLength - item.numMinutes)
                setChangeLength(newLength - item.numMinutes)
            },
            onDragEnd: (state) => {
                if (activeDragType !== 'end') return
                let newNumMinutes = changeLength + item.numMinutes
                calendarState.editSelectedItem(
                    {
                        numMinutes: Math.max(newNumMinutes, 15),
                    },
                    true
                )
                calendarState.adjustDragMinutes(0)
                setChangeLength(0)
                calendarState.setDragging(false)
                setActiveDragType(null)
            },
        })
        const bodyDrag = useGesture({
            onDragStart: () => {
                if (activeDragType !== null) return
                setActiveDragType('body')
                calendarState.setDragging(true)
                calendarState.selectItem(item, itemType)
                setClickOffset(
                    calendarState.calendarMouseFloatMinutes - item.startMinutes
                )
            },
            onDrag: (state) => {
                if (activeDragType !== 'body') return
                const newY =
                    calendarState.calendarMouseFloatMinutes -
                    item.startMinutes -
                    clickOffset
                setChangeY(newY)

                // During drag, we'll update the item's position visually
                // The actual date update will happen on dragEnd
                const currentDayIndex = Math.floor(
                    calendarState.calendarMouseX /
                        (calendarState.calendarWidth / 7)
                )
                const originalDiffDays = differenceInDays(
                    item.date,
                    calendarState.weekStart
                )

                // Always update the drag day index if it's a valid day
                if (currentDayIndex >= 0 && currentDayIndex < 7) {
                    // If we're back to the original day, set dragDayIndex to -1 to use original positioning
                    if (currentDayIndex === originalDiffDays) {
                        setDragDayIndex(-1)
                    } else {
                        setDragDayIndex(currentDayIndex)
                    }
                }
            },
            onDragEnd: (state) => {
                if (activeDragType !== 'body') return
                let newStartMinutes = changeY + item.startMinutes

                // Get the current day index based on mouse position
                const dayIndex = Math.floor(
                    calendarState.calendarMouseX /
                        (calendarState.calendarWidth / 7)
                )

                // Calculate the new date based on the day index
                const newDate = addDays(calendarState.weekStart, dayIndex)

                // Only update the date if it's different from the current date
                // Always update the startMinutes
                const currentDate = item.date
                const sameDay = isSameDay(currentDate, newDate)

                if (sameDay) {
                    calendarState.editSelectedItem(
                        {
                            startMinutes: newStartMinutes,
                        },
                        true
                    )
                } else {
                    calendarState.editSelectedItem(
                        {
                            startMinutes: newStartMinutes,
                            date: newDate,
                        },
                        true
                    )
                }

                // Reset the drag day index
                setDragDayIndex(-1)

                setChangeY(0)
                calendarState.setDragging(false)
                setActiveDragType(null)
            },
        })
        const calDrag = useGesture({
            onDragStart: (state) => {
                if (activeDragType !== null) return
                setActiveDragType('cal')
                calendarState.deselectItem()
                calendarState.setCalendarMouseData(
                    mouse.x,
                    mouse.y,
                    mouse.elementWidth,
                    mouse.elementHeight
                )
            },
            onDrag: (state) => {
                if (activeDragType !== 'cal') return
                if (!calendarState.newItem) {
                    if (
                        state.movement[1] /
                            (calendarState.calendarMinuteEms * 10) >
                        7.5
                    ) {
                        calendarState.createNewItem(true)
                    }
                } else {
                    const numMinutes =
                        calendarState.calendarMouseFloatMinutes -
                        calendarState.newItem.startMinutes
                    calendarState.updateNewItem({
                        numMinutes,
                    })
                }
            },
            onDragEnd: (state) => {
                if (activeDragType !== 'cal') return
                if (calendarState.newItem) {
                    let numMinutes =
                        calendarState.calendarMouseFloatMinutes -
                        calendarState.newItem.startMinutes
                    const newItem = calendarState.commitNewItem(
                        {
                            numMinutes: Math.max(numMinutes, 15),
                        },
                        true
                    )
                    setTimeout(() => {
                        calendarState.selectItem(newItem)
                    }, 100)
                }
                setActiveDragType(null)
            },
        })
        return (
            diffDays < 7 &&
            diffDays >= 0 && (
                <div
                    className={
                        `calendar-item ${align}` +
                        (selected ? ' selected' : '') +
                        (itemType === 'allocation' ? ' allocation' : '') +
                        (activeDragType === 'body' && dragDayIndex !== -1
                            ? ' dragging-between-days'
                            : '')
                    }
                    style={{
                        width: `calc(${100 / 7}% - 2rem - 2px)`,
                        height: `calc(${
                            calendarState.calendarMinuteEms * length
                        }rem - 2px)`,
                        left:
                            activeDragType === 'body' && dragDayIndex !== -1
                                ? `calc(${(100 / 7) * dragDayIndex}% + ${align === 'left' ? '1px' : '2rem + 1px'})`
                                : align === 'left'
                                  ? `calc(${(100 / 7) * diffDays}% + 1px)`
                                  : `calc(${(100 / 7) * diffDays}% + 2rem + 1px)`,
                        top: `calc(${
                            calendarState.calendarMinuteEms * y
                        }rem + 1px)`,
                        zIndex:
                            zIndex +
                            (selected
                                ? 3
                                : hovering && !calendarState.dragging
                                  ? 2
                                  : 0),
                        opacity:
                            opacity +
                            ((selected || hovering) && !calendarState.dragging
                                ? 0.4
                                : 0),
                        cursor: !disabled ? 'default' : 'default',
                    }}
                    onPointerOver={(e) => setHovering(true)}
                    onPointerOut={(e) => setHovering(false)}
                    onClick={(e) => {
                        e.stopPropagation()
                        calendarState.selectItem(item, itemType)
                    }}
                    {...(calendarState.mode === 'time' &&
                    itemType === 'allocation'
                        ? calDrag()
                        : {})}
                    {...props}
                >
                    {!disabled && (
                        <div
                            className="body-drag-area"
                            style={{
                                position: 'absolute',
                                top: '1rem',
                                bottom: '1rem',
                                left: 0,
                                right: 0,
                                cursor: 'move',
                                zIndex: 1,
                            }}
                            onClick={(e) => e.stopPropagation()}
                            {...bodyDrag()}
                        ></div>
                    )}
                    {!disabled && (
                        <svg
                            className="vertical-drag"
                            style={{
                                width: '100%',
                                height: '1rem',
                                position: 'absolute',
                                top: '-0.5rem',
                                left: 0,
                                cursor: 'ns-resize',
                                zIndex: 2,
                            }}
                            onClick={(e) => e.stopPropagation()}
                            {...startDrag()}
                        ></svg>
                    )}
                    {!disabled && (
                        <svg
                            className="vertical-drag"
                            style={{
                                width: '100%',
                                height: '1rem',
                                position: 'absolute',
                                bottom: '-0.5rem',
                                left: 0,
                                cursor: 'ns-resize',
                                zIndex: 2,
                            }}
                            onClick={(e) => e.stopPropagation()}
                            {...endDrag()}
                        ></svg>
                    )}
                    <div
                        style={{
                            fontSize: '0.65rem',
                            position: 'relative',
                            zIndex: 0,
                        }}
                    >
                        {item.project?.title}
                    </div>
                    <div
                        style={{
                            fontSize: '0.65rem',
                            position: 'relative',
                            zIndex: 0,
                        }}
                    >
                        {item.projectPhase?.title}
                    </div>
                    {settings.useTasks && (
                        <div
                            style={{
                                fontSize: '0.65rem',
                                position: 'relative',
                                zIndex: 0,
                            }}
                        >
                            {item.task?.name}
                        </div>
                    )}
                    <div
                        className="time"
                        style={{
                            pointerEvents: 'none',
                            position: 'relative',
                            zIndex: 0,
                        }}
                    >{`${Math.floor(
                        Math.round(item.numMinutes + changeLength) / 60
                    )}:${
                        Math.round(item.numMinutes + changeLength) % 60 < 10
                            ? (Math.round(item.numMinutes + changeLength) %
                                  60) +
                              '0'
                            : Math.round(item.numMinutes + changeLength) % 60
                    }`}</div>
                </div>
            )
        )
    }
)
